/**
 * デッキ抽選（/lottery。Issue #953）。デッキ管理からも試合からも独立したタブ。手元にあるデッキ（`PickDuelDecks`）から 2 つ選び、
 * そのままデュエルを作れる（デュエルはその時点の最新のバージョンに固定される）。
 */
import { useEffect, useState } from 'react';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';

export default function DeckLottery() {
  usePageTitle('抽選');
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);   // [deck | null, deck | null]
  const [locked, setLocked] = useState([false, false]);
  const [loading, setLoading] = useState(false);
  const [duelName, setDuelName] = useState('');
  const [duelPlayers, setDuelPlayers] = useState([null, null]);
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.project('GetPlayersList').then(setPlayers); }, []);

  const toggle = (name) => setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  const toggleLock = (i) => setLocked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const pick = async () => {
    setLoading(true);
    const needPick = result ? locked.map((l) => !l) : [true, true];
    const count = needPick.filter(Boolean).length;
    try {
      const params = selected.length ? { players: selected } : {};
      const fetched = await Promise.all(Array.from({ length: count }, () => api.project('PickDuelDecks', params)));
      const next = [result?.[0] ?? null, result?.[1] ?? null];
      let fi = 0;
      needPick.forEach((need, i) => { if (need) next[i] = fetched[fi++]?.[0] ?? null; });
      setResult(next);
      setLocked((prev) => prev.map((l, i) => (needPick[i] ? false : l)));
      setDuelPlayers((prev) => next.map((deck, i) => (needPick[i] ? (deck?.player ?? null) : prev[i])));
    } catch (err) {
      alert('抽選に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasResult = result?.some(Boolean);
  const bothReady = result?.[0] && result?.[1];

  const createDuel = async () => {
    if (!duelName.trim() || !bothReady) return;
    setCreating(true);
    try {
      await api.project('AddDuel', {
        name: duelName.trim(), player1_id: duelPlayers[0], deck1_id: result[0].id, player2_id: duelPlayers[1], deck2_id: result[1].id,
      });
      alert(`デュエル「${duelName.trim()}」を作成しました`);
      setDuelName('');
    } catch (err) {
      alert('デュエル作成に失敗しました: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">デッキ抽選</h1>
      </div>
      <p className="card-meta" style={{ marginBottom: 8 }}>手元にあるデッキから選びます。持ち主で絞れます（選ばなければ全員）。選ばれたデッキを押すと確定して、残りだけ引き直せます。</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {players.map((p) => (
          <button key={p.name} className={`btn btn-sm ${selected.includes(p.name) ? 'btn-primary' : 'btn-secondary'}`} onClick={() => toggle(p.name)}>
            {p.kanji || p.name}
          </button>
        ))}
      </div>
      {/* 2 枚を並べて見比べるのが主眼なので縦に積まない */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 16, minHeight: 180 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ textAlign: 'center', flex: '1 1 0', minWidth: 0, maxWidth: 200 }}>
            {loading && !locked[i] ? (
              <p style={{ color: '#aaa', paddingTop: 40 }}>抽選中...</p>
            ) : result?.[i] ? (
              <div className="card" style={{ cursor: 'pointer', outline: locked[i] ? '2px solid #e94560' : '2px solid transparent', borderRadius: 6 }}
                onClick={() => toggleLock(i)}>
                {result[i].deck_illust_thumbnail_url && (
                  <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', borderRadius: 4, backgroundColor: '#1a1a2e' }}>
                    <img src={result[i].deck_illust_thumbnail_url} alt={result[i].name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div className="card-title" style={{ marginTop: 6 }}>{result[i].name}</div>
                <div className="card-meta">{result[i].player}</div>
                {locked[i] && <div style={{ fontSize: '0.75rem', color: '#e94560', marginTop: 4 }}>確定</div>}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={pick} disabled={loading || locked.every(Boolean)}>
          {loading ? '抽選中...' : hasResult ? (locked.some(Boolean) ? '未確定を再抽選' : '再抽選') : '抽選する'}
        </button>
      </div>
      {bothReady && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[0, 1].map((i) => (
              <div key={i} className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>プレイヤー{i + 1}（{result[i].name}）</label>
                <select className="form-select" value={duelPlayers[i] ?? ''}
                  onChange={(e) => setDuelPlayers((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}>
                  {players.map((p) => <option key={p.name} value={p.name}>{p.kanji || p.name}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="form-input" style={{ flex: 1 }} placeholder="デュエル名" value={duelName} onChange={(e) => setDuelName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createDuel(); }} />
            <button className="btn btn-success" onClick={createDuel} disabled={creating || !duelName.trim()}>{creating ? '作成中...' : 'デュエルを作成'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
